import {
  createFileRoute,
  Outlet,
  redirect,
  useRouter
} from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useRef } from 'react'
import { parse } from 'valibot'

import { PageLayout } from '@/components/layouts'
import { LABELS } from '@/constants/common/message.constants'
import { getDefaultUserMeTenant } from '@/features/auth/models'
import { usePageNavigation, useSortNavigation } from '@/features/shared/hooks'
import { applyDefaultDateRange } from '@/features/shared/models'
import { fetchShipmentOrders } from '@/features/shipment-orders/api'
import { USER_TYPE } from '@/libs/api/types'
import { safeFormatISO } from '@/libs/utils'

import { selectedDcIdAtom, tenantStore } from '../-states'

import {
  ShipmentOrdersHeader,
  ShipmentOrdersSearchForm,
  ShipmentOrdersTable
} from './-components'
import {
  queryFrom,
  shipmentOrdersSearchSchema,
  type ShipmentOrdersSearch
} from './-models'

// 呼び出すたびに日付を初期化
const getDefaultSearch = (): ShipmentOrdersSearch => ({
  shippingDateFrom: safeFormatISO(new Date()),
  shippingDateTo: safeFormatISO(new Date())
})

export const Route = createFileRoute('/_protected/$tenantKey/shipment-orders')({
  meta: () => [{ title: LABELS.SHIPMENT_ORDER }],
  validateSearch: (search) => parse(shipmentOrdersSearchSchema, search),
  loaderDeps: ({ search }) => search,
  beforeLoad: ({ search, context: { auth } }) => {
    if (auth.user.type !== USER_TYPE.OWNER) {
      throw redirect({
        to: `/${getDefaultUserMeTenant(auth.user).key}`
      })
    }
    const selectedDcId = tenantStore.get(selectedDcIdAtom)
    if (search.dcId != selectedDcId) {
      throw redirect({
        to: location.pathname,
        search: {
          ...search,
          dcId: selectedDcId
        }
      })
    }
  },
  loader: async ({
    abortController,
    params: { tenantKey },
    deps,
    context: { auth }
  }) => {
    // 初回レンダリング時の初期値
    const initialSearch = applyDefaultDateRange(getDefaultSearch(), deps)

    const [shipmentOrders] = await Promise.all([
      fetchShipmentOrders(
        { tenantKey, query: queryFrom(initialSearch) },
        abortController.signal
      )
    ])
    return {
      shipmentOrders,
      authUser: auth.user,
      initialSearch
    }
  },
  component: ShipmentOrdersPage,
  // 存在しないパスの場合にOutletで404ページが表示されてしまうのでnullを返す
  notFoundComponent: () => null
})

function ShipmentOrdersPage() {
  const {
    authUser,
    shipmentOrders: { data, pagination },
    initialSearch
  } = Route.useLoaderData()

  const search = Route.useSearch()
  const { tenantKey } = Route.useParams()
  const navigate = Route.useNavigate()
  const router = useRouter()

  const handleDataRefresh = useCallback(async () => {
    await router.invalidate()
  }, [router])

  const handleSubmit = useCallback(
    (search: ShipmentOrdersSearch) => {
      navigate({ search })
    },
    [navigate]
  )

  const handlePageChange = usePageNavigation(search, navigate)

  const handleSortChange = useSortNavigation(search, navigate)

  const selectedDcId = useAtomValue(selectedDcIdAtom)
  const currentDcId = useRef(selectedDcId)

  useEffect(() => {
    if (currentDcId.current !== selectedDcId) {
      currentDcId.current = selectedDcId
      navigate({
        search: {
          dcId: selectedDcId
        }
      })
    }
  }, [search, navigate, selectedDcId])

  return (
    <>
      <PageLayout>
        <div className="flex flex-col gap-2" data-testid="shipment-orders-page">
          <ShipmentOrdersHeader tenantKey={tenantKey} search={initialSearch} />
          <ShipmentOrdersSearchForm
            authUser={authUser}
            tenantKey={tenantKey}
            search={initialSearch}
            defaultSearch={getDefaultSearch()}
            onSubmit={handleSubmit}
            preservedTake={pagination?.take}
          />
          <ShipmentOrdersTable
            tenantKey={tenantKey}
            authUser={authUser}
            shipmentOrders={data}
            pagination={pagination}
            search={search}
            onChangePage={handlePageChange}
            onChangeSort={handleSortChange}
            onDataRefresh={handleDataRefresh}
          />
        </div>
      </PageLayout>
      <Outlet />
    </>
  )
}
